import { Phone, Email } from '../crm/components/contacts/types';

export const validatePhoneNumber = (phoneNumber: string): boolean => {
  const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
  return phoneRegex.test(phoneNumber);
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Format as (###) ###-####
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  return phone; // Return original if not 10 digits
};

export const validateRequired = (value: string, fieldName: string): string | null => {
  if (!value || value.trim() === '') {
    return `${fieldName} is required`;
  }
  return null;
};

export const validatePropertyName = (name: string): string | null => {
  const requiredError = validateRequired(name, 'Property name');
  if (requiredError) return requiredError;
  
  if (name.length > 50) {
    return 'Property name must be 50 characters or less';
  }
  
  return null;
};

export const validatePropertyDescription = (description: string): string | null => {
  if (description && description.length > 200) {
    return 'Property description must be 200 characters or less';
  }
  
  return null;
};

export const validateAddress = (address: {
  street1: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}): { [key: string]: string } => {
  const errors: { [key: string]: string } = {};
  
  const street1Error = validateRequired(address.street1, 'Street address');
  if (street1Error) errors['address.street1'] = street1Error;
  
  const cityError = validateRequired(address.city, 'City');
  if (cityError) errors['address.city'] = cityError;
  
  const stateError = validateRequired(address.state, 'State');
  if (stateError) errors['address.state'] = stateError;
  
  const zipError = validateRequired(address.zip, 'ZIP code');
  if (zipError) errors['address.zip'] = zipError;
  
  const countryError = validateRequired(address.country, 'Country');
  if (countryError) errors['address.country'] = countryError;
  
  return errors;
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: number | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = window.setTimeout(() => {
      func(...args);
    }, wait);
  };
};

/**
 * Convert boolean to integer for API calls (1 for true, 0 for false)
 */
export const booleanToInt = (value: boolean): number => value ? 1 : 0;

/**
 * Convert integer to boolean for display (1 becomes true, 0 becomes false)
 */
export const intToBoolean = (value: number): boolean => value === 1;

/**
 * Convert a Phone object for API calls (boolean to int)
 */
export const phoneForApi = (phone: Omit<Phone, 'id' | 'contactId'>) => ({
  ...phone,
  isPrimary: booleanToInt(phone.isPrimary)
});

/**
 * Convert a Phone object from API response (int to boolean)
 */
export const phoneFromApi = (phone: any): Phone => ({
  ...phone,
  isPrimary: intToBoolean(phone.isPrimary)
});

/**
 * Convert an Email object for API calls (boolean to int)
 */
export const emailForApi = (email: Omit<Email, 'id' | 'contactId'>) => ({
  ...email,
  isPrimary: booleanToInt(email.isPrimary)
});

/**
 * Convert an Email object from API response (int to boolean)
 */
export const emailFromApi = (email: any): Email => ({
  ...email,
  isPrimary: intToBoolean(email.isPrimary)
});
